import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  NgControl,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { MatFormFieldControl } from '@angular/material/form-field';
import { MaskConstants } from '../../../../../libs/constants/mask-constants';
import { RegexConstants } from '../../../../../libs/constants/regex-constants';
import { TextInputComponent } from './text-input.component';

import { FocusMonitor } from '@angular/cdk/a11y';
import { AutoUnsubscribe } from '@wz-decorators/auto-unsubscribe';
import {
  APP_DATE_FORMAT,
  getDateUtc,
  isDateValid,
} from '@wz-utilities/date.utilities';
import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import { ResourcesService } from '../../../tunnelsante/services/resources/resources.service';

@AutoUnsubscribe()
@Component({
  selector: 'wz-date-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: DateInputComponent,
    },
    {
      provide: TextInputComponent,
      useExisting: DateInputComponent,
    },
  ],
})
export class DateInputComponent
  extends TextInputComponent
  implements OnInit, OnDestroy, Validator
{
  override get controlType() {
    return 'wz-date-input';
  }

  private _minDate!: Date | null;
  @Input()
  set minDate(date: string | Date) {
    if (date) {
      if (typeof date === 'string') {
        this._minDate = startOfDay(getDateUtc(date) ?? new Date());
      } else {
        this._minDate = date;
      }
    } else {
      this._minDate = null;
    }
  }

  private _maxDate!: Date | null;
  @Input()
  set maxDate(date: string | Date) {
    if (date) {
      if (typeof date === 'string') {
        this._maxDate = startOfDay(getDateUtc(date) ?? new Date());
      } else {
        this._maxDate = date;
      }
    } else {
      this._maxDate = null;
    }
  }

  override maskConfig = MaskConstants.DateConfig;

  constructor(
    focusMonitor: FocusMonitor,
    elRef: ElementRef,
    ngCtrl: NgControl,
    private resources: ResourcesService,
  ) {
    super(focusMonitor, elRef, ngCtrl);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  validate(fc: FormControl) {
    const dateValidation: ValidatorFn = (c: AbstractControl) => {
      if (c.value && !isDateValid(getDateUtc(c.value))) {
        return { invalid: 'Bad format' };
      }

      return null;
    };

    const minDateValidator: ValidatorFn = (c: AbstractControl) => {
      if (c.value) {
        const mom: any = getDateUtc(c.value);
        if (isBefore(mom, this._minDate!)) {
          return {
            minDate: this.resources.getResource(
              'FORMS_DATE_MIN',
              format(this._minDate!, APP_DATE_FORMAT),
            ),
          };
        }
      }
      return null;
    };

    const maxDateValidator: ValidatorFn = (c: AbstractControl) => {
      if (c.value) {
        const mom = getDateUtc(c.value);
        if (isAfter(mom, this._maxDate!)) {
          return {
            maxDate: this.resources.getResource(
              'FORMS_DATE_MAX',
              format(this._maxDate!, APP_DATE_FORMAT),
            ),
          };
        }
      }

      return null;
    };

    let validators: ValidatorFn[] = [
      Validators.pattern(RegexConstants.Date),
      dateValidation,
    ];

    // is > to minDate ?
    if (this._minDate) {
      validators.push(minDateValidator);
    }

    // is < to maxDate ?
    if (this._maxDate) {
      validators.push(maxDateValidator);
    }

    const composedValidator = Validators.compose(validators);
    return composedValidator ? composedValidator(fc) : null;
  }
}
